import React, { useState } from 'react';
import { HelpCircle, FileText, Calendar, Scale, AlertCircle, Phone, Mail, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';

const HelpPage = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <div>
      <SEOHelmet 
        title="Help for Pro Se Litigants | Self-Representation Guide | California Courts"
        description="Comprehensive help guide for individuals representing themselves in California courts. Step-by-step instructions for starting cases, filing documents, and navigating court procedures."
        keywords="pro se help, self representation California, court filing help, represent yourself in court, California court procedures, legal forms help"
        canonical="/help"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              color: 'white'
            }}>
              <HelpCircle size={40} />
            </div>
            
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)',
              fontSize: 'clamp(2rem, 4vw, 3rem)'
            }}>
              Help for Self-Represented Litigants
            </h1>
            
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.8'
            }}>
              Representing yourself in court? This guide explains California court procedures and how Legal Document Specialist can help you navigate the process.
            </p>

            <div style={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              padding: '1.5rem',
              marginTop: '2rem',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <AlertCircle size={24} color="#F59E0B" style={{ flexShrink: 0 }} />
                <div>
                  <h3 className="heading-4" style={{ marginBottom: '0.5rem', color: '#92400E' }}>
                    Important Legal Notice
                  </h3>
                  <p style={{ color: '#78350F', lineHeight: '1.6', fontSize: '0.95rem' }}>
                    <strong>Legal Document Specialist is NOT a law firm and cannot provide legal advice.</strong> We provide document preparation, filing, and service support only. For legal advice, consult a licensed California attorney or visit your local legal aid organization.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Quick Navigation
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem'
            }}>
              {[
                { title: 'Starting Your Case', icon: <FileText size={24} />, link: '#starting-case' },
                { title: 'Filing Deadlines', icon: <Calendar size={24} />, link: '#deadlines' },
                { title: 'Representing Yourself', icon: <Scale size={24} />, link: '#self-representation' },
                { title: 'Free Legal Help', icon: <HelpCircle size={24} />, link: '#free-help' },
                { title: 'After Filing', icon: <FileText size={24} />, link: '#after-filing' }
              ].map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  style={{
                    backgroundColor: 'var(--bg-page)',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-medium)',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'transform 0.2s, border-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'var(--border-medium)';
                  }}
                >
                  <div style={{ color: 'var(--brand-primary)' }}>{item.icon}</div>
                  <span className="heading-4" style={{ color: 'var(--text-primary)' }}>
                    {item.title}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Starting Your Case */}
      <section id="starting-case" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              How Do I Start My Court Case?
            </h2>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Step-by-Step Guide to Starting a California Court Case
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {[
                  {
                    step: '1',
                    title: 'Determine the Type of Case',
                    content: 'Identify what type of case you have (small claims, family law, civil, unlawful detainer, etc.). Different case types have different forms and procedures.',
                    note: 'Need help identifying your case type? Visit the California Courts Self-Help Center at courts.ca.gov'
                  },
                  {
                    step: '2',
                    title: 'Get the Correct Forms',
                    content: 'Obtain the proper forms for your case type. Most California court forms are available free online.',
                    resources: [
                      { text: 'California Courts Forms', url: 'https://www.courts.ca.gov/forms.htm' },
                      { text: 'Local Court Forms', note: 'Visit your county superior court website' }
                    ]
                  },
                  {
                    step: '3',
                    title: 'Complete Your Forms',
                    content: 'Fill out all required forms completely and accurately. Type or print clearly in black ink. Make copies before filing.',
                    tips: [
                      'Keep a copy of everything for your records',
                      'Don\'t sign forms until ready to file (some need notarization)',
                      'Double-check names, addresses, and case information',
                      'Leave "case number" blank - the court assigns this'
                    ]
                  },
                  {
                    step: '4',
                    title: 'File Your Forms with the Court',
                    content: 'Submit your completed forms to the appropriate court clerk\'s office. You\'ll need to pay filing fees (fee waivers available for low-income filers).',
                    howWeHelp: 'Legal Document Specialist can file your documents for you! We offer court filing services in all 58 California counties with same-day and next-day options available.'
                  },
                  {
                    step: '5',
                    title: 'Serve the Other Party',
                    content: 'After filing, you must "serve" (deliver) copies of your filed documents to all other parties in the case. This is a legal requirement.',
                    howWeHelp: 'We provide professional process serving throughout California! Our registered, bonded process servers handle service of all legal documents with 95%+ first-attempt success rates.'
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    gap: '1.5rem',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: 'var(--brand-primary)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: 'white',
                      flexShrink: 0
                    }}>
                      {item.step}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 className="heading-4" style={{ marginBottom: '0.75rem', color: 'var(--brand-primary)' }}>
                        {item.title}
                      </h4>
                      <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '1rem' }}>
                        {item.content}
                      </p>
                      
                      {item.note && (
                        <div style={{
                          backgroundColor: '#EFF6FF',
                          border: '1px solid #3B82F6',
                          borderRadius: '6px',
                          padding: '0.75rem',
                          fontSize: '0.9rem',
                          color: '#1E40AF'
                        }}>
                          💡 {item.note}
                        </div>
                      )}
                      
                      {item.resources && (
                        <div style={{ marginTop: '1rem' }}>
                          <strong style={{ fontSize: '0.9rem' }}>Resources:</strong>
                          <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                            {item.resources.map((resource, rIndex) => (
                              <li key={rIndex} style={{ marginBottom: '0.5rem' }}>
                                {resource.url ? (
                                  <a href={resource.url} target="_blank" rel="noopener noreferrer" style={{
                                    color: 'var(--brand-primary)',
                                    textDecoration: 'none',
                                    fontWeight: '500'
                                  }}>
                                    {resource.text} <ExternalLink size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
                                  </a>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>
                                    {resource.text} {resource.note && `- ${resource.note}`}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {item.tips && (
                        <div style={{ marginTop: '1rem' }}>
                          <strong style={{ fontSize: '0.9rem' }}>Important Tips:</strong>
                          <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                            {item.tips.map((tip, tIndex) => (
                              <li key={tIndex} style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {item.howWeHelp && (
                        <div style={{
                          backgroundColor: '#F0FDF4',
                          border: '1px solid #10B981',
                          borderRadius: '6px',
                          padding: '1rem',
                          marginTop: '1rem'
                        }}>
                          <strong style={{ color: '#065F46', fontSize: '0.9rem' }}>✓ How We Can Help:</strong>
                          <p style={{ color: '#047857', marginTop: '0.5rem', fontSize: '0.9rem', lineHeight: '1.6' }}>
                            {item.howWeHelp}
                          </p>
                          <Link to="/place-order" style={{
                            display: 'inline-block',
                            marginTop: '0.75rem',
                            color: 'var(--brand-primary)',
                            fontWeight: '600',
                            textDecoration: 'none',
                            fontSize: '0.9rem'
                          }}>
                            Place Order Now →
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Filing Deadlines */}
      <section id="deadlines" style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              What Are the Deadlines for Filing Court Papers?
            </h2>

            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '2px solid #DC2626',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <AlertCircle size={28} color="#DC2626" style={{ flexShrink: 0 }} />
                <div>
                  <h3 className="heading-3" style={{ marginBottom: '1rem', color: '#991B1B' }}>
                    Missing Deadlines Can Harm Your Case
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                    Court deadlines are STRICT. Missing a deadline can result in your case being dismissed, losing your right to respond, or other serious consequences. Always calendar deadlines immediately and file well before they expire.
                  </p>
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '2rem' }}>
                Common California Court Deadlines
              </h3>

              <div style={{
                display: 'grid',
                gap: '1.5rem'
              }}>
                {[
                  {
                    document: 'Response to Complaint (Answer/Demurrer)',
                    deadline: '30 days from service',
                    notes: 'If served by mail in California, add 5 days. Missing this deadline can result in a default judgment against you.'
                  },
                  {
                    document: 'Service of Complaint',
                    deadline: '60 days from filing',
                    notes: 'Plaintiff must serve the defendant within 60 days of filing the complaint, or the case may be dismissed.'
                  },
                  {
                    document: 'Opposition to Motion',
                    deadline: '9 court days before hearing',
                    notes: 'Must be filed and served at least 9 court days before the hearing (add 5 days if mailing within CA).'
                  },
                  {
                    document: 'Reply to Opposition',
                    deadline: '5 court days before hearing',
                    notes: 'Reply papers must be filed at least 5 court days before the hearing.'
                  },
                  {
                    document: 'Small Claims Appeal',
                    deadline: '30 days from judgment',
                    notes: 'Notice of appeal must be filed within 30 days after the court mails notice of entry of judgment.'
                  },
                  {
                    document: 'Notice of Appeal (Civil)',
                    deadline: '60 days from judgment',
                    notes: 'Strictly jurisdictional - cannot be extended. The appellate court has no authority to hear your appeal if filed late.'
                  }
                ].map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: 'var(--bg-card)',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-medium)'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div>
                        <h4 className="heading-4" style={{ marginBottom: '0.25rem' }}>
                          {item.document}
                        </h4>
                      </div>
                      <div>
                        <span style={{
                          backgroundColor: '#FEE2E2',
                          color: '#991B1B',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}>
                          ⏰ {item.deadline}
                        </span>
                      </div>
                    </div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                      {item.notes}
                    </p>
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: '2rem',
                backgroundColor: '#EFF6FF',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #3B82F6'
              }}>
                <h4 className="heading-4" style={{ marginBottom: '1rem', color: '#1E40AF' }}>
                  Need Help with Court Filing?
                </h4>
                <p className="body-medium" style={{ color: '#1E40AF', lineHeight: '1.8', marginBottom: '1rem' }}>
                  Legal Document Specialist offers same-day and next-day court filing services throughout California. We can help ensure your documents are filed on time.
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <Link to="/court-filing" style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'var(--brand-primary)',
                    color: 'white',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>
                    Learn About Court Filing
                  </Link>
                  <Link to="/resources/california-court-procedures-guide" style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'transparent',
                    color: 'var(--brand-primary)',
                    border: '2px solid var(--brand-primary)',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>
                    Read Court Procedures Guide
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Self-Representation */}
      <section id="self-representation" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              How Do I Represent Myself in Court?
            </h2>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Tips for Self-Representation (Pro Se/Pro Per)
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {[
                  {
                    title: 'Learn Court Rules and Procedures',
                    content: 'Study the California Rules of Court and your local court rules. Courts expect you to follow the same rules as attorneys.',
                    resources: ['California Rules of Court: courts.ca.gov/rules', 'Your county superior court website for local rules']
                  },
                  {
                    title: 'Dress Professionally',
                    content: 'Wear business attire to court. First impressions matter, and dressing professionally shows respect for the court.'
                  },
                  {
                    title: 'Be Prepared and Organized',
                    content: 'Bring all documents, evidence, and notes organized in a binder. Arrive early. Know what you want to say and practice beforehand.',
                    tips: ['Make copies of everything', 'Tab important documents', 'Prepare a brief outline of key points', 'Bring relevant case law or statutes if applicable']
                  },
                  {
                    title: 'Address the Judge Properly',
                    content: 'Always address the judge as "Your Honor." Stand when speaking unless told otherwise. Wait your turn to speak and never interrupt.'
                  },
                  {
                    title: 'Stick to the Facts',
                    content: 'Focus on facts, not emotions. Present evidence to support your position. Avoid arguing or being combative with the other party or judge.'
                  },
                  {
                    title: 'Take Notes',
                    content: 'Write down what the judge says, especially orders and deadlines. Ask the clerk for clarification after the hearing if needed.'
                  },
                  {
                    title: 'Follow All Court Orders',
                    content: 'Comply with every court order exactly as stated. Failure to follow orders can result in sanctions or adverse rulings.'
                  }
                ].map((tip, index) => (
                  <div key={index} style={{
                    paddingBottom: '1.5rem',
                    borderBottom: index < 6 ? '1px solid var(--border-light)' : 'none'
                  }}>
                    <h4 className="heading-4" style={{ marginBottom: '0.75rem', color: 'var(--brand-primary)' }}>
                      {index + 1}. {tip.title}
                    </h4>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '0.75rem' }}>
                      {tip.content}
                    </p>
                    {tip.resources && (
                      <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
                        {tip.resources.map((resource, rIndex) => (
                          <li key={rIndex} style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                            {resource}
                          </li>
                        ))}
                      </ul>
                    )}
                    {tip.tips && (
                      <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
                        {tip.tips.map((t, tIndex) => (
                          <li key={tIndex} style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                            {t}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              padding: '2rem'
            }}>
              <h4 className="heading-4" style={{ marginBottom: '1rem', color: '#92400E' }}>
                ⚖️ Consider Limited Scope Representation
              </h4>
              <p className="body-medium" style={{ color: '#78350F', lineHeight: '1.8' }}>
                Even if you can't afford full representation, many attorneys offer "limited scope" or "unbundled" services where they help with specific parts of your case (reviewing documents, coaching, appearing for one hearing, etc.). This can be more affordable than full representation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Free Legal Help */}
      <section id="free-help" style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Where Can I Get Free Legal Help?
            </h2>

            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '3rem', lineHeight: '1.8' }}>
              Many free and low-cost resources are available in California for people who cannot afford an attorney:
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem'
            }}>
              {[
                {
                  title: 'California Courts Self-Help Centers',
                  description: 'Free in-person help at most courthouses. Staff can help you understand forms and procedures (but cannot give legal advice).',
                  link: 'https://www.courts.ca.gov/selfhelp.htm',
                  linkText: 'Find Your Local Self-Help Center'
                },
                {
                  title: 'Legal Aid Organizations',
                  description: 'Free legal services for low-income individuals in civil matters. Each county has legal aid offices.',
                  link: 'https://www.lawhelpca.org',
                  linkText: 'Find Legal Aid in Your County'
                },
                {
                  title: 'State Bar Lawyer Referral Service',
                  description: 'Low-cost initial consultation ($25-$50) with a private attorney who can advise on your case.',
                  link: 'https://www.calbar.ca.gov/Public/Free-Legal-Information/Lawyer-Referral-Service',
                  linkText: 'Get Lawyer Referral'
                },
                {
                  title: 'Law School Clinics',
                  description: 'Many California law schools offer free legal clinics where supervised students provide assistance.',
                  note: 'Search "[your city] law school legal clinic" to find programs near you'
                },
                {
                  title: 'Pro Bono Programs',
                  description: 'Volunteer attorney programs provide free representation in certain types of cases.',
                  link: 'https://www.calbar.ca.gov/Public/Free-Legal-Information/Pro-Bono-Programs',
                  linkText: 'Learn About Pro Bono Services'
                },
                {
                  title: 'Court Fee Waivers',
                  description: 'If you receive public benefits or have low income, you may qualify for a fee waiver (no court filing fees).',
                  note: 'Ask the court clerk for form FW-001 (Fee Waiver Request)'
                }
              ].map((resource, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {resource.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '1rem' }}>
                    {resource.description}
                  </p>
                  {resource.link && (
                    <a 
                      href={resource.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'var(--brand-primary)',
                        fontWeight: '600',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {resource.linkText} <ExternalLink size={14} />
                    </a>
                  )}
                  {resource.note && (
                    <p style={{
                      fontSize: '0.85rem',
                      color: 'var(--text-secondary)',
                      fontStyle: 'italic',
                      marginTop: '0.5rem'
                    }}>
                      💡 {resource.note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: After Filing */}
      <section id="after-filing" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              What Happens After I File My Papers?
            </h2>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '2rem' }}>
                Typical Timeline After Filing
              </h3>

              <div style={{ position: 'relative', paddingLeft: '3rem' }}>
                <div style={{
                  position: 'absolute',
                  left: '23px',
                  top: '50px',
                  bottom: '50px',
                  width: '2px',
                  backgroundColor: 'var(--brand-primary)'
                }}></div>

                {[
                  {
                    title: 'Immediately After Filing',
                    content: 'The court clerk assigns a case number and stamps your documents "filed." You receive copies with the file stamp.',
                    action: 'Make copies of stamped documents for service'
                  },
                  {
                    title: 'Within Days of Filing',
                    content: 'You must serve the filed documents on all other parties in the case. Keep proof of service.',
                    action: 'File proof of service with the court after completing service',
                    help: 'We can serve your documents! Professional process serving available statewide.'
                  },
                  {
                    title: '30 Days Later (Typical)',
                    content: 'The defendant has 30 days to respond (may vary by case type). If no response, you may be able to request a default.',
                    action: 'Monitor deadlines carefully'
                  },
                  {
                    title: 'Scheduling Conference/Hearing',
                    content: 'The court will schedule hearings, conferences, or trial dates. You\'ll receive notice by mail.',
                    action: 'Calendar all hearing dates immediately. Prepare documents and evidence.'
                  },
                  {
                    title: 'Discovery Period',
                    content: 'Both sides exchange information and evidence (interrogatories, document requests, depositions).',
                    action: 'Respond to discovery requests within deadlines (usually 30 days)'
                  },
                  {
                    title: 'Pre-Trial Motions',
                    content: 'Either party may file motions (to dismiss, for summary judgment, etc.). Hearing dates will be set.',
                    action: 'Respond to motions filed against you within deadlines'
                  },
                  {
                    title: 'Trial or Settlement',
                    content: 'Case proceeds to trial, or parties reach a settlement agreement. Many cases settle before trial.',
                    action: 'Prepare trial binder with all evidence and witness information'
                  },
                  {
                    title: 'Judgment',
                    content: 'The court issues a judgment or order. This becomes final unless appealed.',
                    action: 'Comply with all terms of the judgment'
                  }
                ].map((phase, index) => (
                  <div key={index} style={{
                    position: 'relative',
                    marginBottom: '3rem'
                  }}>
                    <div style={{
                      position: 'absolute',
                      left: '-2.25rem',
                      width: '24px',
                      height: '24px',
                      backgroundColor: 'var(--brand-primary)',
                      borderRadius: '50%',
                      border: '3px solid var(--bg-card)'
                    }}></div>
                    
                    <h4 className="heading-4" style={{ marginBottom: '0.75rem', color: 'var(--brand-primary)' }}>
                      {phase.title}
                    </h4>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '0.75rem' }}>
                      {phase.content}
                    </p>
                    <div style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #10B981',
                      borderRadius: '6px',
                      padding: '0.75rem',
                      fontSize: '0.9rem'
                    }}>
                      <strong style={{ color: '#065F46' }}>Your Action:</strong>
                      <span style={{ color: '#047857', marginLeft: '0.5rem' }}>{phase.action}</span>
                    </div>
                    {phase.help && (
                      <div style={{
                        marginTop: '0.5rem',
                        backgroundColor: '#EFF6FF',
                        border: '1px solid #3B82F6',
                        borderRadius: '6px',
                        padding: '0.75rem',
                        fontSize: '0.85rem',
                        color: '#1E40AF'
                      }}>
                        ✓ {phase.help}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Can Help */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              How Legal Document Specialist Can Help
            </h2>

            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '3rem', textAlign: 'center', lineHeight: '1.8' }}>
              While we cannot provide legal advice, we offer essential document services to support self-represented litigants:
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem'
            }}>
              {[
                {
                  service: 'Court Filing',
                  description: 'We file your completed documents with any California court. Same-day and next-day filing available.',
                  link: '/court-filing'
                },
                {
                  service: 'Process Serving',
                  description: 'Professional service of legal documents throughout California. Registered, bonded servers with 95%+ success rate.',
                  link: '/process-serving'
                },
                {
                  service: 'Document Retrieval',
                  description: 'Retrieve court records, case files, and other documents from courts and government agencies.',
                  link: '/document-retrieval'
                },
                {
                  service: 'County Recording',
                  description: 'Record deeds, liens, and other legal instruments at any California county recorder\'s office.',
                  link: '/county-recording'
                },
                {
                  service: 'Skip Tracing',
                  description: 'Locate parties for service of process when their address is unknown.',
                  link: '/skip-tracing'
                }
              ].map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  style={{
                    backgroundColor: 'var(--bg-page)',
                    padding: '2rem',
                    borderRadius: '12px',
                    border: '1px solid var(--border-medium)',
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'transform 0.2s, border-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'var(--border-medium)';
                  }}
                >
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {service.service}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '1rem' }}>
                    {service.description}
                  </p>
                  <span style={{ color: 'var(--brand-primary)', fontWeight: '600', fontSize: '0.9rem' }}>
                    Learn More →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Still Have Questions?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Contact us to learn how we can support your self-representation journey
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Request a Quote
            </a>
            <Link 
              to="/contact"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Mail size={20} />
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HelpPage;
